import { useEffect, useState } from "react";
import api from "../../api";

const PLATFORM_ICONS = {
  instagram: "fab fa-instagram",
  tiktok:    "fab fa-tiktok",
  facebook:  "fab fa-facebook",
};

function Stars({ count }) {
  return (
    <div className="flex gap-0.5 text-primary text-base mb-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} style={{ opacity: i < count ? 1 : 0.25 }}>★</span>
      ))}
    </div>
  );
}

function PostSkeleton() {
  return (
    <div className="aspect-square rounded-2xl bg-primary/5 animate-pulse border border-dashed border-primary/20" />
  );
}

function ReviewSkeleton() {
  return (
    <div className="rounded-2xl bg-white dark:bg-[#1e1a17] p-6 shadow-sm animate-pulse space-y-3">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-4/5" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
      <div className="flex items-center gap-3 pt-2">
        <div className="w-9 h-9 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="space-y-1 flex-1">
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-16" />
        </div>
      </div>
    </div>
  );
}

function InstagramCard({ post }) {
  const iconClass = PLATFORM_ICONS[post.platform] || "fab fa-instagram";

  return (
    <a
      href={post.postUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`View post by ${post.username}`}
      className="group relative block aspect-square rounded-2xl overflow-hidden border border-dashed border-primary/30 bg-primary/5 hover:border-primary/60 transition-all duration-300 hover:shadow-md"
    >
      {/* Platform icon top-right */}
      <span className="absolute top-2.5 right-2.5 z-10 text-[#181411]/30 group-hover:text-primary transition-colors">
        <i className={`${iconClass} text-base`} />
      </span>

      {post.imageUrl ? (
        <img
          src={post.imageUrl}
          alt={post.username}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-[#181411]/30">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="m21 15-5-5L5 21" />
          </svg>
          <span className="text-xs font-medium">{post.username}</span>
        </div>
      )}

      {post.imageUrl && (
        <div className="absolute inset-0 bg-[#181411]/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
          <span className="text-white text-xs font-semibold">{post.username}</span>
        </div>
      )}
    </a>
  );
}

function ReviewCard({ review }) {
  const isGoogle = review.platform === "google";

  return (
    <a
      href={review.reviewUrl || "#"}
      target={review.reviewUrl ? "_blank" : "_self"}
      rel="noopener noreferrer"
      aria-label={`Read review by ${review.author}`}
      className="group flex flex-col bg-white dark:bg-[#1e1a17] rounded-2xl p-6 shadow-sm border border-transparent hover:border-primary/30 hover:shadow-md transition-all duration-300 cursor-pointer"
    >
      <Stars count={review.stars} />
      <p className="text-[#181411]/80 dark:text-white/70 text-sm sm:text-base leading-relaxed italic flex-1">
        "{review.text}"
      </p>
      <div className="flex items-center gap-3 mt-5">
        <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <span className="text-primary font-bold text-sm">
            {review.author[0].toUpperCase()}
          </span>
        </div>
        <div>
          <p className="text-[#181411] dark:text-white text-sm font-semibold leading-tight">
            {review.author}
          </p>
          {review.handle && (
            <p className="text-[#181411]/50 dark:text-white/40 text-xs">{review.handle}</p>
          )}
        </div>
        <span className="ml-auto text-[#181411]/20 group-hover:text-[#4285F4] transition-colors">
          {isGoogle ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          ) : (
            <i className={`${PLATFORM_ICONS[review.platform] || "fas fa-star"} text-lg`} />
          )}
        </span>
      </div>
    </a>
  );
}

const Community = () => {
  const [posts, setPosts]       = useState([]);
  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/community-posts"),
      api.get("/reviews"),
    ])
      .then(([postsRes, reviewsRes]) => {
        setPosts(postsRes.data?.data ?? postsRes.data ?? []);
        setReviews(reviewsRes.data?.data ?? reviewsRes.data ?? []);
      })
      .catch(() => {
        // silently fail — section simply stays empty
      })
      .finally(() => setLoading(false));
  }, []);

  const visiblePosts   = posts.filter((p) => p.visible !== false);
  const visibleReviews = reviews.filter((r) => r.visible !== false);

  // Don't render the section at all if both are empty after loading
  if (!loading && visiblePosts.length === 0 && visibleReviews.length === 0) {
    return null;
  }

  return (
    <section
      id="community"
      className="w-full py-16 sm:py-24 bg-background-light dark:bg-background-dark/50"
    >
      <div className="px-4 md:px-10 lg:px-20 mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-[#181411] dark:text-white text-3xl sm:text-4xl font-display font-bold leading-tight mb-3">
            Join the community
          </h2>
          <p className="text-[#181411]/60 dark:text-white/50 text-base sm:text-lg max-w-md mx-auto">
            Our customers share their best bites. Tag{" "}
            <a
              href="https://www.instagram.com/pisu.tn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-semibold hover:underline"
            >
              @pisu.tn
            </a>{" "}
            to appear here.
          </p>
          <a
            href="https://www.instagram.com/pisu.tn/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 text-primary font-semibold text-sm hover:underline"
          >
            <i className="fab fa-instagram text-base" />
            @pisu.tn
          </a>
        </div>

        {/* Community posts grid */}
        {(loading || visiblePosts.length > 0) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-16">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => <PostSkeleton key={i} />)
              : visiblePosts.map((post) => <InstagramCard key={post.id} post={post} />)
            }
          </div>
        )}

        {/* Reviews */}
        {(loading || visibleReviews.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {loading
              ? Array.from({ length: 3 }).map((_, i) => <ReviewSkeleton key={i} />)
              : visibleReviews.map((review) => <ReviewCard key={review.id} review={review} />)
            }
          </div>
        )}
      </div>
    </section>
  );
};

export default Community;
